import type { AnswerValue, StoredAnswer, Via } from './questions';
import { getBlockingConditions, tiempoEspanaIsPositive } from './questions';

export interface EvaluationResult {
  eligible: boolean;
  issues: string[];
  warnings: string[];
  positives: string[];
  familyNotes: string[];
  practicalInfo: string[];
}

type Lang = 'es' | 'en';

const ISSUE_LABELS: Record<string, { es: string; en: string }> = {
  asilo_fecha: {
    es: 'No solicitaste asilo antes de la fecha límite. Es requisito imprescindible.',
    en: 'You did not apply for asylum before the deadline. This is a mandatory requirement.',
  },
  tiempo_espana: {
    es: 'No te encuentras en España o no llevas suficiente tiempo de permanencia continuada.',
    en: 'You are not in Spain or you do not have enough continuous stay time.',
  },
  estado_excluyente: {
    es: 'Tu situación actual es incompatible con esta vía de regularización (residencia vigente, otro trámite en curso, protección temporal Ucrania, prohibición Schengen, amenaza al orden público o asilo previo).',
    en: 'Your current situation is incompatible with this regularization path (valid permit, ongoing procedure, Ukraine temporary protection, Schengen ban, threat to public order, or previous asylum).',
  },
  pasaporte: {
    es: 'No dispones de pasaporte. Es necesario un documento de identidad válido.',
    en: 'You do not have a passport. A valid identity document is required.',
  },
};

function tx(es: string, en: string, lang: Lang): string {
  return lang === 'en' ? en : es;
}

function asArray(answer: StoredAnswer | undefined): AnswerValue[] {
  return Array.isArray(answer) ? answer : [];
}

function evaluateAntecedentes(answer: StoredAnswer | undefined, lang: Lang) {
  switch (answer) {
    case 'vigentes':
      return {
        warning: tx(
          'Tienes antecedentes penales vigentes. Esto puede ser motivo de denegación.',
          'You have active criminal records. This may be grounds for denial.',
          lang
        ),
      };
    case 'cancelable':
      return {
        warning: tx(
          'Tienes antecedentes cancelados o cancelables. Solicita el certificado actualizado.',
          'You have expunged or expungeable records. Request an updated certificate.',
          lang
        ),
      };
    case 'noSure':
      return {
        warning: tx(
          'No estás seguro/a sobre tus antecedentes. Solicita un certificado de antecedentes penales.',
          "You're not sure about your records. Request a criminal record certificate.",
          lang
        ),
      };
    case 'no':
      return {
        positive: tx('Sin antecedentes penales.', 'No criminal record.', lang),
      };
    default:
      return {};
  }
}

function evaluatePasaporte(answer: StoredAnswer | undefined, lang: Lang) {
  switch (answer) {
    case 'yes':
      return { positive: tx('Tienes pasaporte en vigor.', 'You have a valid passport.', lang) };
    case 'caducado':
      return {
        warning: tx(
          'Tu pasaporte está caducado. Deberás renovarlo para presentar la solicitud.',
          'Your passport is expired. You will need to renew it to submit the application.',
          lang
        ),
      };
    default:
      return {};
  }
}

function evaluateAsiloFecha(answer: StoredAnswer | undefined, lang: Lang) {
  if (answer === 'dontRemember') {
    return {
      warning: tx(
        'No recuerdas la fecha de tu solicitud de asilo. Verifica con tu abogado o en el resguardo de presentación.',
        "You don't remember your asylum application date. Check with your lawyer or submission receipt.",
        lang
      ),
    };
  }
  if (answer === 'yes') {
    return {
      positive: tx(
        'Tienes solicitud de asilo presentada en plazo.',
        'You have an asylum application submitted on time.',
        lang
      ),
    };
  }
  return {};
}

function evaluateDocumentacionLaboral(answer: StoredAnswer | undefined, lang: Lang) {
  switch (answer) {
    case 'contrato':
      return {
        positive: tx(
          'Cuentas con contrato de trabajo vigente.',
          'You have a current employment contract.',
          lang
        ),
      };
    case 'oferta':
      return {
        positive: tx(
          'Cuentas con oferta de empleo formal.',
          'You have a formal job offer.',
          lang
        ),
      };
    case 'autonomo':
      return {
        positive: tx(
          'Cuentas con un proyecto de trabajo autónomo.',
          'You have a self-employment project.',
          lang
        ),
      };
    case 'cotizaciones':
      return {
        positive: tx(
          'Acreditas cotizaciones previas a la Seguridad Social.',
          'You can prove previous Social Security contributions.',
          lang
        ),
      };
    case 'no':
      return {
        warning: tx(
          'No tienes documentación laboral. Necesitarás conseguir una oferta de empleo o contrato.',
          'You do not have employment documentation. You will need to obtain a job offer or contract.',
          lang
        ),
      };
    default:
      return {};
  }
}

function evaluateVulnerabilidad(answer: StoredAnswer | undefined, lang: Lang) {
  if (!answer || answer === 'na') {
    return {
      warning: tx(
        'No puedes acreditar vulnerabilidad actualmente. Necesitarás documentación acreditativa.',
        'You cannot currently prove vulnerability. You will need supporting documentation.',
        lang
      ),
    };
  }
  return {
    positive: tx(
      'Cuentas con documentación para acreditar tu situación de vulnerabilidad.',
      'You have documentation to prove your vulnerability.',
      lang
    ),
  };
}

function familyNoteFor(option: AnswerValue, lang: Lang): string | null {
  switch (option) {
    case 'hijos_menores':
      return tx(
        'Tus hijos menores podrían obtener autorización de residencia derivada.',
        'Your minor children could obtain a derived residence permit.',
        lang
      );
    case 'hijos_discapacidad':
      return tx(
        'Tus hijos con discapacidad podrían beneficiarse de la regularización.',
        'Your children with disabilities could benefit from regularization.',
        lang
      );
    case 'conyuge':
    case 'padres':
      return tx(
        'Tus familiares directos en España refuerzan tu solicitud y podrían beneficiarse de la extensión.',
        'Your direct family members in Spain strengthen your application and could benefit from the extension.',
        lang
      );
    default:
      return null;
  }
}

export function evaluate(
  via: Via,
  answers: Record<string, StoredAnswer>,
  lang: string = 'es'
): EvaluationResult {
  const l: Lang = lang === 'en' ? 'en' : 'es';
  const blockingConditions = getBlockingConditions(via);

  const issues: string[] = [];
  const warnings: string[] = [];
  const positives: string[] = [];
  const familyNotes: string[] = [];

  // ── Blockers ──
  for (const condition of blockingConditions) {
    const answer = answers[condition.questionId];
    const blocked =
      (condition.blockingAnswer !== undefined && answer === condition.blockingAnswer) ||
      (condition.blockingPredicate !== undefined && condition.blockingPredicate(answer));
    if (blocked) {
      const label = ISSUE_LABELS[condition.questionId];
      issues.push(label ? label[l] : condition.questionId);
    }
  }

  // ── Antecedentes ──
  const ant = evaluateAntecedentes(answers.antecedentes, l);
  if (ant.warning) warnings.push(ant.warning);
  if (ant.positive) positives.push(ant.positive);

  // ── Asilo fecha (DT5) ──
  if (via === 'dt5') {
    const af = evaluateAsiloFecha(answers.asilo_fecha, l);
    if (af.warning) warnings.push(af.warning);
    if (af.positive) positives.push(af.positive);
  }

  // ── Pasaporte (DT6) ──
  if (via === 'dt6') {
    const ps = evaluatePasaporte(answers.pasaporte, l);
    if (ps.warning) warnings.push(ps.warning);
    if (ps.positive) positives.push(ps.positive);
  }

  // ── Documentacion laboral (DT6, supuesto !== C) ──
  if (via === 'dt6' && answers.supuesto !== 'C') {
    const dl = evaluateDocumentacionLaboral(answers.documentacion_laboral, l);
    if (dl.warning) warnings.push(dl.warning);
    if (dl.positive) positives.push(dl.positive);
  }

  // ── Vulnerabilidad (DT6, supuesto === C) ──
  if (via === 'dt6' && answers.supuesto === 'C') {
    const vu = evaluateVulnerabilidad(answers.acreditacion_vulnerabilidad, l);
    if (vu.warning) warnings.push(vu.warning);
    if (vu.positive) positives.push(vu.positive);
  }

  // ── Estado excluyente (warning informativo si DT6 marcó asilo_previo solo) ──
  const estado = asArray(answers.estado_excluyente);
  if (
    via === 'dt6' &&
    estado.length === 1 &&
    estado[0] === 'asilo_previo'
  ) {
    // Solo asilo_previo → bloqueante, pero recordar que la DT5 puede aplicar
    warnings.push(
      tx(
        'Has solicitado asilo previamente. La DT 5a podría ser más adecuada para tu caso.',
        'You have previously applied for asylum. TD 5th might be more suitable for your case.',
        l
      )
    );
  }

  // ── Generic positives ──
  if (issues.length === 0) {
    positives.unshift(
      tx('Cumples los requisitos básicos.', 'You meet the basic requirements.', l)
    );
  }

  if (tiempoEspanaIsPositive(answers.tiempo_espana)) {
    positives.push(
      tx(
        'Acreditas permanencia continuada en España.',
        'You can prove continuous stay in Spain.',
        l
      )
    );
  }

  // ── Family notes (multiselect) ──
  const familia = asArray(answers.familia);
  for (const opt of familia) {
    const note = familyNoteFor(opt, l);
    if (note && !familyNotes.includes(note)) familyNotes.push(note);
  }

  if (answers.expediente_expulsion === 'yes') {
    familyNotes.push(
      tx(
        'Al presentar la solicitud, el procedimiento de expulsión puede quedar suspendido.',
        'By filing the application, the deportation procedure may be suspended.',
        l
      )
    );
  }

  // ── Practical info (siempre) ──
  const practicalInfo =
    l === 'es'
      ? [
          'Plazo límite: 30 de junio de 2026',
          'Presentar en la Oficina de Extranjería o por sede electrónica',
          'Formulario: modelo EX-10',
          'El resguardo de solicitud autoriza a permanecer y trabajar',
          'Silencio administrativo positivo a los 6 meses',
          'Autorización inicial de 1 año de duración',
        ]
      : [
          'Deadline: June 30, 2026',
          'Submit at the Immigration Office or online',
          'Form: model EX-10',
          'The application receipt authorizes you to stay and work',
          'Positive administrative silence after 6 months',
          'Initial authorization for 1 year',
        ];

  return {
    eligible: issues.length === 0,
    issues,
    warnings,
    positives,
    familyNotes,
    practicalInfo,
  };
}
